import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaArrowLeft } from "react-icons/fa6";
import Swal from "sweetalert2";
import moment from "moment";
import dayjs from "dayjs";
import styles from "./staffattendence.module.css";
import AxiosInstance, { TokenService } from "../../../services/Api";
import CustomMRTTable from "../../../components/CustomMRTTable/CustomMRTTable";
import { CustomButton, CustomDatePicker } from "../../../components";

const StaffAttendanceListView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { staffId } = useParams();
  const { translations } = useSelector((state) => state.language);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [staffName, setStaffName] = useState(location.state?.staffName || "");
  const [rowCount, setRowCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");

  // Date filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Date errors
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  useEffect(() => {
    if (staffId) {
      fetchAttendanceLogs();
    }
  }, [pagination.pageIndex, pagination.pageSize, staffId]);

  // Format date to YYYY-MM-DD using moment
  const formatDate = (date) => {
    if (!date) return "";
    return moment(date).format("YYYY-MM-DD");
  };

  const fetchAttendanceLogs = async (fromDate, toDate) => {
    setLoading(true);

    const from = fromDate !== undefined ? fromDate : startDate;
    const to = toDate !== undefined ? toDate : endDate;

    try {
      const payload = {
        staff_id: Number(staffId),
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      };

      const formattedFrom = formatDate(from);
      const formattedTo = formatDate(to);

      if (formattedFrom) payload.from_date = formattedFrom;
      if (formattedTo) payload.to_date = formattedTo;

      const response = await AxiosInstance.post(
        "/attendance/staff-attendance-log",
        payload,
      );

      if (response?.data?.status) {
        setData(response.data.data || []);
        setRowCount(response.data.total || response.data.data?.length || 0);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching staff attendance logs", error);
      setData([]);
      setRowCount(0);
      Swal.fire({
        icon: "error",
        title: translations?.error || "Error",
        text: error.message || "Failed to fetch attendance logs",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (val) => {
    setStartDate(val);
    setStartDateError("");
    // Reset end date if it's before the new start date
    if (endDate && dayjs(val).isAfter(dayjs(endDate))) {
      setEndDate(null);
      setEndDateError("");
    }
  };

  const handleEndDateChange = (val) => {
    setEndDate(val);
    setEndDateError("");
  };

  // Validate both dates are selected
  const validateDates = () => {
    let isValid = true;

    if (!startDate) {
      setStartDateError(
        translations?.fromDateRequired || "Start Date is required",
      );
      isValid = false;
    }

    if (!endDate) {
      setEndDateError(translations?.toDateRequired || "End Date is required");
      isValid = false;
    }

    return isValid;
  };

  const handleSearch = () => {
    if (!validateDates()) return;

    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    fetchAttendanceLogs(startDate, endDate);
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setStartDateError("");
    setEndDateError("");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    fetchAttendanceLogs(null, null);
  };

  const columns = useMemo(
    () => [
      {
        id: "sno",
        header: translations?.serialNo || "S. No",
        size: 80,
        Cell: ({ row }) => (
          <div
            style={{ textAlign: "center", fontWeight: 600, fontSize: "16px" }}
          >
            {row.index + 1 + pagination.pageIndex * pagination.pageSize}
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "date",
        header: translations?.date || "Date",
        size: 150,
        Cell: ({ cell }) => (
          <div
            style={{ textAlign: "center", fontWeight: 600, fontSize: "16px" }}
          >
            {cell.getValue()}
          </div>
        ),
      },
      {
        accessorKey: "status",
        header: translations?.status || "Status",
        size: 120,
        Cell: ({ cell }) => (
          <div
            style={{ textAlign: "center", fontWeight: 600, fontSize: "16px" }}
          >
            {cell.getValue()?.toUpperCase()}
          </div>
        ),
      },
      {
        accessorKey: "time",
        header: translations?.time || "Time",
        size: 150,
        Cell: ({ cell }) => (
          <div
            style={{ textAlign: "center", fontWeight: 600, fontSize: "16px" }}
          >
            {cell.getValue()}
          </div>
        ),
      },
    ],
    [pagination.pageIndex, pagination.pageSize, translations],
  );

  return (
    <>
      <div className="container-fluid py-4">
        {/* Breadcrumbs and Back Button */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-6">
            <ol className={`breadcrumb ${styles.breadcrumbs}`}>
              {/* <li className="breadcrumb-item">
                <a href="/school/dashboard" className={styles.breadcrumbLink}>
                  {translations?.dashboard || "Dashboard"}
                </a>
              </li> */}
              <li
                className={`breadcrumb-item active ${styles.breadcrumbActive}`}
              >
                {translations?.attendanceLogs || "Attendance Logs"}:{" "}
                <strong>{staffName}</strong>
              </li>
            </ol>
          </div>
          <div className="col-md-6 text-end">
            <CustomButton
              variant="contained"
              className={styles.backButton}
              onClick={() => navigate(-1)}
            >
              <FaArrowLeft className="me-2" size={14} />
              {translations?.back || "Back"}
            </CustomButton>
          </div>
        </div>

        {/* Date Filters */}
        <div className="row mb-4 align-items-end">
          <div className="col-md-4 px-2">
            <CustomDatePicker
              label={translations?.startDate || "Start Date"}
              value={startDate ? dayjs(startDate) : null}
              onChange={handleStartDateChange}
              placeholder={
                translations?.startDatePlaceholder || "Select start date"
              }
              error={startDateError}
              required
              disabled={loading}
            />
          </div>
          <div className="col-md-4 px-2">
            <CustomDatePicker
              label={translations?.endDate || "End Date"}
              value={endDate ? dayjs(endDate) : null}
              onChange={handleEndDateChange}
              placeholder={
                translations?.endDatePlaceholder || "Select end date"
              }
              minDate={startDate ? dayjs(startDate) : undefined}
              error={endDateError}
              required
              disabled={loading}
            />
          </div>
          <div className="col-md-4 px-2 d-flex gap-2 mb-3">
            <CustomButton
              variant="contained"
              className={styles.filterBtn}
              onClick={handleSearch}
            >
              <i className="material-icons me-2">search</i>
              {translations?.searchButton || "Search"}
            </CustomButton>
            <CustomButton
              variant="outlined"
              className={styles.clearBtn}
              onClick={handleClear}
            >
              <i className="material-icons me-2">refresh</i>
              {translations?.clear || "Clear"}
            </CustomButton>
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableCard}>
          <CustomMRTTable
            columns={columns}
            data={data}
            loading={loading}
            pagination={pagination}
            rowCount={rowCount}
            onPaginationChange={setPagination}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            onSearchEnter={(value) => {
              setApiSearch(value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            enableSearch={true}
            enableFiltering={false}
            placeholder={translations?.searchName || "Search ..."}
          />
        </div>
      </div>
    </>
  );
};

export default StaffAttendanceListView;
